import { type FC, MouseEvent, ReactNode } from 'react';
import { Drawer, Modal, ModalProps } from 'antd';
import { DrawerStyles } from 'antd/es/drawer/DrawerPanel';
import { styled } from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { Title } from '@/shared/uikit/Title';

type StyleProps = { minHeight?: string; maxHeight?: string };
type ModalSlideContainerProps = ModalProps &
  StyleProps & { drawerHeight?: string; drawerStyles?: DrawerStyles };

export const ModalSlideContainer: FC<ModalSlideContainerProps> = ({
  footer,
  drawerHeight,
  title,
  drawerStyles,
  ...props
}) => {
  const isDesktop = useIsDesktop();

  const adaptTitle = (
    <Title bold size={isDesktop ? 22 : 18}>
      {title}
    </Title>
  );

  return isDesktop ? (
    <StyledModal title={adaptTitle} style={{ padding: 0 }} {...props} footer={footer} />
  ) : (
    <StyledDrawer
      {...props}
      title={adaptTitle}
      onClose={(e) => props.onCancel?.(e as MouseEvent<HTMLButtonElement>)}
      placement="bottom"
      height={drawerHeight || '90vh'}
      footer={footer as ReactNode}
      style={{
        borderRadius: '16px 16px 0 0',
      }}
      styles={drawerStyles}
    />
  );
};

// ===== styles =====

const StyledDrawer = styled(Drawer)`
  border-radius: 24px 24px 0 0 !important;

  .ant-drawer-header {
    border-bottom: 0 !important;
  }

  .ant-drawer-footer {
    border-top: 0 !important;
  }

  .ant-drawer-body {
    padding: 0 !important;
  }

  &&& .ant-drawer-header {
    padding-top: 24px;
    padding-inline: 16px;
  }

  &&& .ant-drawer-close {
    position: absolute;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    inset-inline-end: 20px;
    top: none;
    margin: 0;
  }

  &&& .ant-drawer-title {
    display: flex;
    justify-content: center;
  }

  &&& .ant-drawer-title:before {
    position: absolute;
    top: 9px;
    content: '';
    height: 4px;
    width: 40px;
    border-radius: 6px;
    background: ${COLORS.systemDark50A};
  }
`;

const StyledModal = styled(Modal)<StyleProps>`
  &&& {
    .ant-modal-content {
      border-radius: 12px;
      padding: 16px 0px 0px 0px;
    }
  }

  &&& .ant-modal-header {
    padding-inline: 24px;
  }
`;
